import { Component } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { reportClientCrash } from '@/components/common/reportClientCrash';
import { getPublicSystemStrings } from './publicFunnelLanguage';

/**
 * WEB-1988 — граница падения публичного входа.
 *
 * Делает то же, что `components/common/AppErrorBoundary` в кабинете: вместо
 * белого экрана — текст из словаря (пара ru/en) и кнопка перезагрузки, плюс
 * отчёт на сервер (`POST /health/client-error` — счётчик падений и детали в
 * админ-панели). Отличий два, и оба вынужденные:
 *
 *  1. Ant Design (`Result`, `Button`) заменён своей разметкой — кабинетный
 *     экран падения стоил бы покупателю всего antd в первом чанке.
 *  2. `@sentry/react` НЕ импортируется статически. SDK вместе с записью
 *     сессии — десятки килобайт до первого экрана; на публичном входе он
 *     поднимается лениво (см. `src/public-main.tsx`). Если к моменту падения
 *     он уже поднялся — падение уходит и в него тоже, через тот же ленивый
 *     импорт (модуль один и тот же, второй загрузки не будет). Не поднялся —
 *     остаётся серверный отчёт, который и так был единственным каналом при
 *     незаданном VITE_SENTRY_DSN.
 */
interface PublicErrorBoundaryProps {
  children: ReactNode;
}

interface PublicErrorBoundaryState {
  hasError: boolean;
}

function handleReload(): void {
  window.location.reload();
}

function ErrorFallback(): ReactElement {
  // WEB-2022: тексты покупателю — из словаря ВОРОНКИ на языке воронки или
  // браузера покупателя (сторож WEB-967), не из словаря кабинета (ru/en).
  const s = getPublicSystemStrings();
  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        minHeight: '60vh',
        padding: '48px 24px',
        textAlign: 'center',
      }}
    >
      <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>{s.crashTitle}</h1>
      <p
        style={{
          margin: 0,
          color: 'var(--color-text-secondary, rgba(0, 0, 0, 0.65))',
        }}
      >
        {s.crashSubtitle}
      </p>
      <button
        type="button"
        onClick={handleReload}
        style={{
          marginTop: 8,
          padding: '8px 16px',
          fontSize: 14,
          color: '#fff',
          background: 'var(--color-primary, #4094F7)',
          border: 'none',
          borderRadius: 'var(--border-radius, 8px)',
          cursor: 'pointer',
        }}
      >
        {s.reloadPage}
      </button>
    </div>
  );
}

export class PublicErrorBoundary extends Component<
  PublicErrorBoundaryProps,
  PublicErrorBoundaryState
> {
  state: PublicErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): PublicErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: unknown): void {
    reportClientCrash({
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
      url: window.location.href,
    });

    // Ленивый — см. п.2 в шапке файла. Отчёт не должен уронить саму границу.
    import('@sentry/react')
      .then((Sentry) => {
        if (Sentry.getClient()) Sentry.captureException(error);
      })
      .catch(() => {
        /* нет сети/чанка — остаётся серверный отчёт выше */
      });
  }

  render(): ReactNode {
    return this.state.hasError ? <ErrorFallback /> : this.props.children;
  }
}

export default PublicErrorBoundary;
